'use client';

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { RiCloseLine } from '@remixicon/react';
import { useTranslations } from 'next-intl';
import { LEGAL_DOC_SLUGS } from '@mentorivo/shared';

import { DashSwitch } from '@/components/dashboard/dash-switch';
import { CenterLayer } from '@/components/ui/center-layer';
import { useToast } from '@/components/ui/toast';
import { Link, usePathname } from '@/i18n/navigation';
import {
  CONSENT_PREFERENCES_EVENT,
  OPTIONAL_CATEGORIES,
  consentAllows,
  readConsent,
  saveConsent,
} from '@/lib/cookie-consent';
import type { ConsentDraft } from '@/lib/cookie-consent';
import { dur, ease } from '@/lib/motion';
import { siteLinks } from '@/lib/site-links';

/**
 * ÇEREZ DUVARI — karar verilmeden siteye girilemeyen tam ekran katman.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 Kapanmıyor: karar ZORUNLU, ama seçim GERÇEK
 * ═══════════════════════════════════════════════════════════════════════════
 * Esc kapatmıyor, perdeye tıklamak kapatmıyor, kapatma düğmesi yok, arkadaki
 * sayfa ne kaydırılabiliyor ne de Tab'la gezilebiliyor (odak tuzağı
 * `CenterLayer`de). Kullanıcı üç şeyden birini yapmak zorunda: tümünü kabul,
 * yalnızca gerekli, ya da kendi seçimi.
 *
 * ⚠️ "Yalnızca gerekli" seçeneği bu tasarımın HUKUKİ dayanağı — kaldırılamaz.
 * Reddetmenin kabul etmek kadar kolay olmadığı bir duvar (yalnız "Kabul et"
 * düğmesi taşıyan, ya da reddetmeyi ikinci bir ekrana saklayan) KVKK/GDPR
 * anlamında geçerli bir onay üretmiyor. İki düğme aynı satırda, aynı boyda ve
 * aynı tıklama sayısında duruyor; biri öne çıkarılacaksa gerekçesi hukuk
 * danışmanından gelmeli, tasarımdan değil.
 *
 * ---------------------------------------------------------------------------
 * Karar burada DEĞİL, `lib/cookie-consent.ts`te
 * ---------------------------------------------------------------------------
 * Bu dosya yalnızca soruyu soruyor ve cevabı o kütüphaneye veriyor. Bir
 * betiğin yüklenip yüklenmeyeceği burada değil, o betiği yükleyen yerde
 * `consentAllows(...)` ile okunuyor.
 *
 * ---------------------------------------------------------------------------
 * Kabuk havuzdan: `CenterLayer`
 * ---------------------------------------------------------------------------
 * Odak tuzağı, kaydırma kilidi, Esc, odak dönüşü ve portal orada bir kez
 * yazılı. Dördü de sessiz hata üreten şeyler ("Tab beni sayfanın arkasına
 * attı" diye kimse bildirmez) ve ikinci bir kopya yazmak onları ikiye
 * bölerdi. Buraya iki prop eklendi: kendi perdesi (`dash-modal-scrim` yalnız
 * panelde tanımlı) ve boy animasyonu.
 *
 * ---------------------------------------------------------------------------
 * Sunucuda hiç basılmıyor
 * ---------------------------------------------------------------------------
 * Görünürlük çereze bağlı, sayfalar ise statik üretiliyor. Kararı sunucuda
 * okumak `cookies()` demek ve bu, sitenin TAMAMINI dinamik render'a çevirirdi.
 * Bu yüzden duvar hidrasyondan sonra doğuyor — ilk boyamada bir kare boyunca
 * sayfa görünüyor, sonra perde iniyor.
 */

/**
 * Duvarın ÇIKMADIĞI sayfalar — yasal belgeler.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 Bu istisna olmadan duvar kendi bağlantısını kilitliyor
 * ═══════════════════════════════════════════════════════════════════════════
 * Duvar "Çerez Politikası"na bağlantı veriyor. O sayfada da çıksaydı kullanıcı
 * okumaya gittiği metni göremez, önüne yine aynı soru gelirdi — okumadan karar
 * vermeye zorlanmış olurdu. Bilgilendirmenin onaydan ÖNCE erişilebilir olması
 * KVKK/GDPR'ın şartı; teknik bir incelik değil.
 *
 * Liste `@mentorivo/shared`ten OKUNUYOR, elle kopyalanmıyor: aynı dört slug
 * `site-links.ts`te ve dört sayfa dosyasında da yazılı ve beşinci bir kopya,
 * yeni bir yasal belge eklendiğinde sessizce eski kalırdı.
 */
const LEGAL_PATHS: ReadonlySet<string> = new Set(LEGAL_DOC_SLUGS.map((slug) => `/${slug}`));

type Mode = 'hidden' | 'notice' | 'preferences';

export function CookieConsent() {
  const t = useTranslations('cookieConsent');
  const toast = useToast();
  const rowId = useId();

  const [mode, setMode] = useState<Mode>('hidden');
  const [draft, setDraft] = useState<ConsentDraft>({ analytics: false, marketing: false });

  /**
   * Duvarın NEDEN açık olduğu.
   *
   * `auto`   : ilk ziyaret, henüz karar yok. Kapatma düğmesi YOK ve olmamalı.
   * `footer` : kullanıcı kayıtlı kararını değiştirmeye geldi. Burası artık bir
   *            ayar yüzeyi; vazgeçip çıkabilmeli, yoksa footer'daki bağlantıya
   *            yanlışlıkla basan biri yeniden karar vermeye mecbur kalır.
   *            Kaydetme de burada bir SONUÇ, o yüzden bildirim çıkıyor — ilk
   *            ziyarette çıkmıyor, duvarın kalkması zaten cevabın kendisi.
   */
  const [source, setSource] = useState<'auto' | 'footer'>('auto');

  /* Kapatma isteği `CenterLayer`in odak etkisine bağımlılık olarak giriyor ve
     kimliği DEĞİŞMEMELİ (gerekçe orada, `onClose` prop'unun başında). Bu
     yüzden güncel durum geri çağırmanın içinden değil ref'ten okunuyor. */
  const state = useRef({ mode, source });
  state.current = { mode, source };

  /* Adres dile göre önek taşımıyor (`usePathname` locale'i soyuyor), yani
     /tr/cookies de /en/cookies de burada `/cookies` görünüyor. */
  const onLegalPage = LEGAL_PATHS.has(usePathname());

  useEffect(() => {
    /* Kullanıcı ayarları kendi açtıysa karışma — sayfa değişse bile panel
       onun elinde. */
    if (state.current.source === 'footer') return;

    setMode(onLegalPage || readConsent() ? 'hidden' : 'notice');
  }, [onLegalPage]);

  /* Footer girişi: kayıtlı karar varsa anahtarlar onunla açılıyor — panel
     "şu an neye izin veriyorsun" sorusunun cevabını göstermek zorunda. */
  useEffect(() => {
    function onOpen() {
      const current = readConsent();
      setDraft({
        analytics: consentAllows(current, 'analytics'),
        marketing: consentAllows(current, 'marketing'),
      });
      setSource('footer');
      setMode('preferences');
    }

    window.addEventListener(CONSENT_PREFERENCES_EVENT, onOpen);
    return () => window.removeEventListener(CONSENT_PREFERENCES_EVENT, onOpen);
  }, []);

  /**
   * Esc ve perdeye tıklama buraya düşüyor — ama çoğu zaman HİÇBİR ŞEY
   * yapmıyor:
   *
   *   • ilk ziyaret + soru ekranı → kapanmıyor. Cevapsız kapanan bir duvar
   *     her sayfada yeniden çıkar ve kullanıcı ondan hiç kurtulamaz.
   *   • ilk ziyaret + ayar paneli → soru ekranına geri dönüyor.
   *   • footer'dan açılmış → kapanıyor; orada zaten kayıtlı bir karar var.
   */
  const requestClose = useCallback(() => {
    const { mode: current, source: origin } = state.current;

    if (origin === 'footer') {
      setSource('auto');
      setMode('hidden');
    } else if (current === 'preferences') {
      setMode('notice');
    }
  }, []);

  const commit = useCallback(
    (next: ConsentDraft) => {
      saveConsent(next);
      setMode('hidden');

      if (state.current.source === 'footer') {
        setSource('auto');
        toast.ok(t('saved'));
      }
    },
    [t, toast],
  );

  const open = mode === 'preferences';

  return (
    <CenterLayer
      open={mode !== 'hidden'}
      onClose={requestClose}
      labelledBy={`${rowId}-title`}
      className="cc-card"
      layerClassName="cc-layer"
      scrimClassName="cc-scrim"
      animateResize
    >
      {/* Kapatma yalnız AYAR yüzeyinde. İlk ziyarette basılmıyor: orada bir
          soru var ve cevaplanması gerekiyor. */}
      {source === 'footer' ? (
        <button type="button" className="cc-close" aria-label={t('close')} onClick={requestClose}>
          <RiCloseLine size={16} aria-hidden="true" />
        </button>
      ) : null}

      {/* `layout="position"` : kutu uzarken başlık ve cümle ESNEMESİN. Yalnız
          kutuya `layout` verilseydi metin de ölçeklenir, harfler bir kare
          boyunca yassılaşırdı. */}
      <motion.div className="cc-head" layout="position">
        <h2 className="cc-title" id={`${rowId}-title`}>
          {t('title')}
        </h2>
        <p className="cc-text">{t('text')}</p>
        {siteLinks.cookies.live ? (
          <Link className="cc-link" href={siteLinks.cookies.href}>
            {t('policy')}
          </Link>
        ) : null}
      </motion.div>

      <AnimatePresence initial={false}>
        {open ? (
          <motion.ul
            key="rows"
            className="cc-rows"
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              transition: { duration: dur.base, ease: ease.out, delay: 0.06 },
            }}
            exit={{ opacity: 0, transition: { duration: dur.instant, ease: ease.in } }}
          >
            <ConsentRow
              id={`${rowId}-necessary`}
              title={t('categories.necessary.title')}
              note={t('categories.necessary.note')}
              state={t('locked')}
              on
              locked
            />
            {OPTIONAL_CATEGORIES.map((category) => (
              <ConsentRow
                key={category}
                id={`${rowId}-${category}`}
                title={t(`categories.${category}.title`)}
                note={t(`categories.${category}.note`)}
                on={draft[category]}
                onToggle={() =>
                  setDraft((current) => ({ ...current, [category]: !current[category] }))
                }
              />
            ))}
          </motion.ul>
        ) : null}
      </AnimatePresence>

      <motion.div className="cc-actions" layout="position">
        {open ? (
          <>
            <button type="button" className="cc-btn cc-btn--primary" onClick={() => commit(draft)}>
              {t('save')}
            </button>
            <button
              type="button"
              className="cc-btn cc-btn--ghost"
              onClick={() => commit({ analytics: true, marketing: true })}
            >
              {t('acceptAll')}
            </button>
          </>
        ) : (
          <>
            {/* İki düğme aynı boyda ve aynı satırda — gerekçe dosya başlığında:
                reddetmek kabul etmek kadar kolay olmak ZORUNDA. */}
            <button
              type="button"
              className="cc-btn cc-btn--primary"
              onClick={() => commit({ analytics: true, marketing: true })}
            >
              {t('acceptAll')}
            </button>
            <button
              type="button"
              className="cc-btn cc-btn--ghost"
              onClick={() => commit({ analytics: false, marketing: false })}
            >
              {t('rejectAll')}
            </button>
            <button
              type="button"
              className="cc-btn cc-btn--quiet"
              onClick={() => setMode('preferences')}
            >
              {t('customize')}
            </button>
          </>
        )}
      </motion.div>
    </CenterLayer>
  );
}

/**
 * Tek kategori satırı.
 *
 * Anahtar duvarın kendi çizimi DEĞİL, ürünün tek aç/kapa anahtarı
 * (`DashSwitch`). Derisi bu iş için `dashboard.css`ten `globals.css`e taşındı:
 * aynı anahtar artık panelin dışında da gerekiyor.
 */
function ConsentRow({
  id,
  title,
  note,
  state,
  on,
  locked,
  onToggle,
}: {
  id: string;
  title: string;
  note: string;
  /** Kilitli satırda anahtarın yerine okunan yazı ("Her zaman açık"). */
  state?: string;
  on: boolean;
  locked?: boolean;
  onToggle?: () => void;
}) {
  return (
    <li className="cc-row">
      <div className="cc-row-text">
        <span className="cc-row-title" id={id}>
          {title}
        </span>
        <span className="cc-row-note">{note}</span>
      </div>
      {/* Kilitli kategori için sönük bir anahtar değil YAZI basılıyor: kapalı
          görünen ama basılamayan bir denetim "bozuk mu?" sorusu doğuruyor,
          "Her zaman açık" ise cevabın kendisi. */}
      {locked ? (
        <span className="cc-row-locked">{state}</span>
      ) : (
        <DashSwitch on={on} labelledBy={id} onToggle={onToggle ?? (() => {})} />
      )}
    </li>
  );
}
